<template>
  <div class="hierarchical-report">
    <!-- Field Details View -->
    <FieldDetailsView
      v-if="viewingFieldId"
      :fieldId="viewingFieldId"
      @close="closeFieldDetails"
    />

    <!-- Main Report View -->
    <template v-else>
      <!-- Loading State -->
      <div v-if="loading" class="loading-container">
        <i class="fa fa-circle-o-notch fa-spin fa-3x fa-fw"></i>
        <p>Loading {{ currentLevelName }} data...</p>
      </div>

      <!-- Filters and Breadcrumb Navigation -->
      <template v-else>
      <div class="filters-section">
        <div class="filter-group">
          <label for="crop-year-filter">Crop Year:</label>
          <select id="crop-year-filter" v-model="selectedCropYear" class="crop-year-select">
            <option value="all">All Years</option>
            <option v-for="year in availableCropYears" :key="year" :value="year">
              {{ year }}
            </option>
          </select>
        </div>

        <div class="filter-group">
          <label for="crop-month-filter">Crop Month:</label>
          <select id="crop-month-filter" v-model="selectedCropMonth" class="crop-year-select">
            <option value="all">All Months</option>
            <option v-for="month in availableCropMonths" :key="month" :value="month">
              {{ getMonthName(month) }}
            </option>
          </select>
        </div>

        <div class="filter-group">
          <label for="crop-type-filter">Crop Type:</label>
          <select id="crop-type-filter" v-model="selectedCropType" class="crop-year-select">
            <option value="all">All Types</option>
            <option value="Corn">Corn</option>
            <option value="Soybeans">Soybeans</option>
            <option value="Cotton">Cotton</option>
            <option value="Rice">Rice</option>
            <option value="Other">Other</option>
          </select>
        </div>

        <div class="filter-group">
          <label for="mode-filter">Field Mode:</label>
          <select id="mode-filter" v-model="selectedMode" class="crop-year-select">
            <option value="all">All Modes</option>
            <option value="1">Furrow</option>
            <option value="2">Levee</option>
          </select>
        </div>

        <div class="filter-group">
          <label>
            <input type="checkbox" v-model="hideUnknowns" />
            Hide Unknown States/Counties
          </label>
        </div>

        <div class="filter-group">
          <button @click="openStateFilter" class="filter-toggle-button">
            <i class="fa fa-filter"></i>
            Filter States ({{ excludedStates.size }} excluded)
          </button>
        </div>

        <div class="filter-group">
          <button @click="downloadCSV" class="download-csv-button" :disabled="loading">
            <i class="fa fa-download"></i>
            Download CSV
          </button>
        </div>

        <span class="data-note">*Data based off hole design creation</span>
      </div>

      <!-- State Filter Panel -->
      <div v-if="showStateFilter" class="state-filter-panel">
        <div class="state-filter-header">
          <h3>Select States to Include</h3>
          <div class="state-filter-actions">
            <button @click="selectAllPendingStates" class="action-button">Select All</button>
            <button @click="deselectAllPendingStates" class="action-button">Deselect All</button>
            <button @click="applyStateFilter" class="action-button accent">Filter</button>
          </div>
        </div>
        <div class="state-filter-list">
          <label v-for="state in availableStates" :key="state" class="state-checkbox">
            <input
              type="checkbox"
              :checked="!pendingExcludedStates.has(state)"
              @change="togglePendingState(state)"
            />
            {{ state }}
          </label>
        </div>
      </div>

      <div class="breadcrumb" v-if="breadcrumbs.length">
        <span
          v-for="(crumb, index) in breadcrumbs"
          :key="index"
          class="breadcrumb-item"
          @click="navigateToBreadcrumb(index)"
        >
          {{ crumb.label }}
          <i v-if="index < breadcrumbs.length - 1" class="fa fa-angle-right"></i>
        </span>
      </div>
      <!-- Report Header -->
      <div class="report-header">
        <h2>{{ currentTitle }}</h2>
        <div class="summary-stats">
          <div class="stat-card" v-for="(value, key) in data?.overall || {}" :key="key">
            <div class="stat-label">{{ formatLabel(key) }}</div>
            <div class="stat-value">{{ formatValue(value) }}</div>
          </div>
        </div>
      </div>
    </template>
    <!-- Data Table -->
    <div v-if="!loading && (data?.byLevel || []).length > 0" class="report-table-container">
      <table class="report-table">
        <thead>
          <tr>
            <th v-for="column in columns" :key="column.key">
              {{ column.label }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in data?.byLevel || []"
            :key="row.id"
            @click="drillDown(row)"
            :class="{ 'clickable-row': canDrillDown }"
          >
            <td v-for="column in columns" :key="column.key">
              <span v-if="column.key === columns[0].key" class="primary-cell">
                <i v-if="canDrillDown" class="fa fa-angle-right"></i>
                {{ row[column.key] }}
              </span>
              <span v-else>{{ formatValue(row[column.key]) }}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

      <!-- Empty State -->
      <div v-if="!loading && (data?.byLevel || []).length === 0" class="empty-state">
        <i class="fa fa-file-text-o fa-3x"></i>
        <p>No data available for this level</p>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, inject, watch } from 'vue'
import useSWRV from 'swrv'
import FieldDetailsView from './FieldDetailsView.vue'

const $api = inject('$api')
const $store = inject('$store')

const currentFarm = ref(null)
const currentFarmName = ref(null)
const viewingFieldId = ref(null)
const viewingFieldName = ref(null)
const currentLevel = ref('national')
const currentState = ref(null)
const currentCounty = ref(null)
const currentUser = ref(null)
const selectedCropYear = ref('all')
const selectedCropType = ref('all')
const selectedCropMonth = ref('all')
const selectedMode = ref('all')
const hideUnknowns = ref(false)
const excludedStates = ref(new Set())
const showStateFilter = ref(false)
const allAvailableCropYears = ref([])
const allAvailableCropMonths = ref([])

const fetcher = async (url) => {
  const response = await $api.fetch(url)
  return response.json()
}

const apiEndpoint = computed(() => {
  const params = new URLSearchParams()

  if (selectedCropYear.value !== 'all') {
    params.append('cropYear', selectedCropYear.value)
  }

  if (selectedCropMonth.value !== 'all') {
    params.append('cropMonth', selectedCropMonth.value)
  }

  if (selectedCropType.value !== 'all') {
    params.append('cropType', selectedCropType.value)
  }

  if (selectedMode.value !== 'all') {
    params.append('mode', selectedMode.value)
  }

  if (excludedStates.value.size > 0) {
    params.append('excludedStates', Array.from(excludedStates.value).join(','))
  }

  if (hideUnknowns.value) {
    params.append('hideUnknowns', 'true')
  }

  const levelConfig = {
    state: { state: currentState },
    county: { state: currentState, county: currentCounty },
    user: { state: currentState, county: currentCounty, userId: currentUser },
    farm: { state: currentState, county: currentCounty, userId: currentUser, farmId: currentFarm }
  }

  const config = levelConfig[currentLevel.value]
  
  if (config && Object.values(config).some(ref => !ref.value)) {
    return null
  }

  // Add level-specific parameters
  if (config) {
    Object.entries(config).forEach(([key, ref]) => {
      params.append(key, ref.value)
    })
  }

  return `/api/reporting/user_acres/summary/${currentLevel.value}?${params.toString()}`
})

const { data, isLoading, isValidating } = useSWRV(apiEndpoint, fetcher, {
  revalidateOnFocus: false,
  dedupingInterval: 60000
})

const loading = computed(() => isLoading.value || isValidating.value)

// Update available crop years when we get unfiltered data
watch(data, (newData) => {
  if (newData?.byLevel) {
    if (selectedCropYear.value === 'all') {
      const years = new Set()
      newData.byLevel.forEach((row) => {
        if (row.cropYears) {
          row.cropYears.forEach((year) => years.add(year))
        }
      })
      const sortedYears = Array.from(years).sort((a, b) => b - a)
      if (sortedYears.length > 0) {
        allAvailableCropYears.value = sortedYears
      }
    }
    
    if (selectedCropMonth.value === 'all') {
      const months = new Set()
      newData.byLevel.forEach((row) => {
        if (row.cropMonths) {
          row.cropMonths.forEach((month) => months.add(month))
        }
      })
      const sortedMonths = Array.from(months).sort((a, b) => a - b)
      if (sortedMonths.length > 0) {
        allAvailableCropMonths.value = sortedMonths
      }
    }
  }
}, { immediate: true })

const availableStates = computed(() => {
  if (currentLevel.value === 'national' && data.value?.availableStates) {
    return data.value.availableStates
  }
  return []
})

const availableCropYears = computed(() => {
  return allAvailableCropYears.value
})

const availableCropMonths = computed(() => {
  return allAvailableCropMonths.value
})

function getMonthName(monthNumber) {
  const cropMonthNames = [
    'August', 'September', 'October', 'November', 'December', 'January',
    'February', 'March', 'April', 'May', 'June', 'July'
  ]
  return cropMonthNames[monthNumber - 1] || monthNumber
}

const currentLevelName = computed(
  () =>
    ({
      national: 'National',
      state: 'State',
      county: 'County',
      user: 'User',
      farm: 'Farm'
    })[currentLevel.value]
)

const currentTitle = computed(() => {
  if (currentLevel.value === 'national') return 'National Totals'
  if (currentLevel.value === 'state') return `${currentState.value || 'State'} - County Totals`
  if (currentLevel.value === 'county')
    return `${currentState.value || 'State'} > ${currentCounty.value || 'County'} - Users`
  if (currentLevel.value === 'user') return `User Details`
  if (currentLevel.value === 'farm') return `Farm Details`
  return 'Report'
})

const columns = computed(() => {
  const map = {
    national: [
      { key: 'state', label: 'State' },
      { key: 'totalAcres', label: 'Total Acres' },
      { key: 'uniqueUserCount', label: 'Users' },
      { key: 'uniqueFarmCount', label: 'Farms' },
      { key: 'furrowCount', label: 'Furrows' },
      { key: 'leveeCount', label: 'Levees' }
    ],
    state: [
      { key: 'county', label: 'County' },
      { key: 'totalAcres', label: 'Total Acres' },
      { key: 'uniqueUserCount', label: 'Users' },
      { key: 'uniqueFarmCount', label: 'Farms' },
      { key: 'furrowCount', label: 'Furrows' },
      { key: 'leveeCount', label: 'Levees' }
    ],
    county: [
      { key: 'userId', label: 'User ID' },
      { key: 'totalAcres', label: 'Total Acres' },
      { key: 'uniqueFarmCount', label: 'Farms' },
      { key: 'uniqueFieldCount', label: 'Fields' },
      { key: 'furrowCount', label: 'Furrows' },
      { key: 'leveeCount', label: 'Levees' }
    ],
    user: [
      { key: 'farmName', label: 'Farm Name' },
      { key: 'totalAcres', label: 'Total Acres' },
      { key: 'fieldCount', label: 'Fields' },
      { key: 'furrowCount', label: 'Furrows' },
      { key: 'leveeCount', label: 'Levees' }
    ],
    farm: [
      { key: 'fieldName', label: 'Field Name' },
      { key: 'totalAcres', label: 'Acres' },
      { key: 'type', label: 'Type' },
      { key: 'cropYears', label: 'Crop Years' }
    ]
  }
  return map[currentLevel.value] ?? []
})

const canDrillDown = computed(() => {
  return true // All levels are now clickable, including fields
})

const breadcrumbs = computed(() => {
  const crumbs = [{ label: 'National', level: 'national' }]
  if (currentState.value) {
    crumbs.push({ label: currentState.value, level: 'state' })
  }
  if (currentCounty.value) {
    crumbs.push({ label: currentCounty.value, level: 'county' })
  }
  if (currentUser.value) {
    crumbs.push({ label: `${currentUser.value}`, level: 'user' })
  }
  if (currentFarm.value) {
    crumbs.push({ label: currentFarmName.value || currentFarm.value, level: 'farm' })
  }
  return crumbs
})

function navigateToBreadcrumb(index) {
  const crumb = breadcrumbs.value[index]

  if (crumb.level === 'national') {
    currentLevel.value = 'national'
    currentState.value = null
    currentCounty.value = null
    currentUser.value = null
    currentFarm.value = null
    currentFarmName.value = null
  } else if (crumb.level === 'state') {
    currentLevel.value = 'state'
    currentCounty.value = null
    currentUser.value = null
    currentFarm.value = null
    currentFarmName.value = null
  } else if (crumb.level === 'county') {
    currentLevel.value = 'county'
    currentUser.value = null
    currentFarm.value = null
    currentFarmName.value = null
  } else if (crumb.level === 'user') {
    currentLevel.value = 'user'
    currentFarm.value = null
    currentFarmName.value = null
  }
}

function drillDown(row) {
  if (currentLevel.value === 'national') {
    currentLevel.value = 'state'
    currentState.value = row.state
  } else if (currentLevel.value === 'state') {
    currentLevel.value = 'county'
    currentCounty.value = row.county
  } else if (currentLevel.value === 'county') {
    currentLevel.value = 'user'
    currentUser.value = row.userId
  } else if (currentLevel.value === 'user') {
    currentLevel.value = 'farm'
    currentFarm.value = row.farmId
    currentFarmName.value = row.farmName
  } else if (currentLevel.value === 'farm') {
    // Open field details view
    viewingFieldId.value = row.fieldId
    viewingFieldName.value = row.fieldName
  }
}

function closeFieldDetails() {
  viewingFieldId.value = null
  viewingFieldName.value = null
}

function formatLabel(key) {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (s) => s.toUpperCase())
    .trim()
}

function formatValue(value) {
  if (typeof value === 'number') {
    return value.toLocaleString()
  }
  if (Array.isArray(value)) {
    return value.join(', ') || '-'
  }
  return value ?? '-'
}

const pendingExcludedStates = ref(new Set())

function openStateFilter() {
  showStateFilter.value = true
  pendingExcludedStates.value = new Set(excludedStates.value)
}

function applyStateFilter() {
  excludedStates.value = new Set(pendingExcludedStates.value)
  showStateFilter.value = false
}

function togglePendingState(state) {
  if (pendingExcludedStates.value.has(state)) {
    pendingExcludedStates.value.delete(state)
  } else {
    pendingExcludedStates.value.add(state)
  }
  pendingExcludedStates.value = new Set(pendingExcludedStates.value)
}

function selectAllPendingStates() {
  pendingExcludedStates.value = new Set()
}

function deselectAllPendingStates() {
  pendingExcludedStates.value = new Set(availableStates.value)
}

async function downloadCSV() {
  const params = new URLSearchParams()
  
  if (selectedCropYear.value !== 'all') {
    params.append('cropYear', selectedCropYear.value)
  }
  if (selectedCropMonth.value !== 'all') {
    params.append('cropMonth', selectedCropMonth.value)
  }
  if (selectedCropType.value !== 'all') {
    params.append('cropType', selectedCropType.value)
  }
  if (selectedMode.value !== 'all') {
    params.append('mode', selectedMode.value)
  }
  if (excludedStates.value.size > 0) {
    params.append('excludedStates', Array.from(excludedStates.value).join(','))
  }
  if (hideUnknowns.value) {
    params.append('hideUnknowns', 'true')
  }
  
  const levelConfig = {
    state: { state: currentState },
    county: { state: currentState, county: currentCounty },
    user: { state: currentState, county: currentCounty, userId: currentUser },
    farm: { state: currentState, county: currentCounty, userId: currentUser, farmId: currentFarm }
  }
  
  const config = levelConfig[currentLevel.value]
  if (config) {
    Object.entries(config).forEach(([key, ref]) => {
      if (ref.value) params.append(key, ref.value)
    })
  }
  
  await $store.dispatch('getReport', { 
    report: 'totals', 
    level: currentLevel.value,
    queryParams: params.toString()
  });
  
  if ($store.state.reports) {
    let link = document.createElement('a')
    link.href = URL.createObjectURL($store.state.reports)
    link.download = `user_acres_${currentLevel.value}_report.csv`
    link.target = "_blank"
    link.type = "text/csv"

    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }
}
</script>
<style scoped>
.hierarchical-report {
  padding: var(--general-padding);
}
 
/* Filters Section */
.filters-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 16px;
  padding: 12px 16px;
  background: var(--menu-background-color);
  border-radius: var(--general-border-radius);
}

.filter-group {
  display: flex;
  align-items: center;
  gap: 12px;
}

.filter-group label {
  font-size: 14px;
  font-weight: 500;
  color: var(--text1);
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.filter-group input[type='checkbox'] {
  cursor: pointer;
}

.filter-toggle-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: var(--accent0);
  border: none;
  border-radius: var(--general-border-radius);
  color: white;
  font-size: 14px;
  cursor: pointer;
  transition: opacity 0.2s;
}

.filter-toggle-button:hover {
  opacity: 0.8;
}

.download-csv-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: var(--accent1);
  border: none;
  border-radius: var(--general-border-radius);
  color: white;
  font-size: 14px;
  cursor: pointer;
  transition: opacity 0.2s;
}

.download-csv-button:hover {
  opacity: 0.8;
}

.download-csv-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.crop-year-select {
  padding: 8px 12px;
  background: var(--bg1);
  border: 1px solid var(--accent0);
  border-radius: var(--general-border-radius);
  color: var(--text1);
  font-size: 14px;
  cursor: pointer;
  min-width: 150px;
}

.crop-year-select:hover {
  border-color: var(--accent1);
}

.crop-year-select:focus {
  outline: none;
  border-color: var(--accent1);
  box-shadow: 0 0 0 2px rgba(29, 154, 214, 0.2);
}

.data-note {
  font-size: 12px;
  color: #999;
  font-style: italic;
}

/* State Filter Panel */
.state-filter-panel {
  margin-bottom: 16px;
  padding: 16px;
  background: var(--menu-background-color);
  border-radius: var(--general-border-radius);
  border: 2px solid var(--accent0);
}

.state-filter-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.state-filter-header h3 {
  margin: 0;
  color: var(--accent0);
  font-size: 16px;
}

.state-filter-actions {
  display: flex;
  gap: 8px;
}

.action-button {
  padding: 6px 12px;
  background: var(--bg1);
  border: 1px solid var(--accent0);
  border-radius: var(--general-border-radius);
  color: var(--text1);
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
}

.action-button:hover {
  background: var(--accent0);
  color: white;
}

.state-filter-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
  max-height: 300px;
  overflow-y: auto;
}

.state-checkbox {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  background: var(--bg1);
  border-radius: var(--general-border-radius);
  cursor: pointer;
  transition: background 0.2s;
}

.state-checkbox:hover {
  background: var(--bg2);
}

.state-checkbox input[type='checkbox'] {
  cursor: pointer;
}

/* Breadcrumb */
.breadcrumb {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
  padding: 12px;
  background: var(--menu-background-color);
  border-radius: var(--general-border-radius);
}

.breadcrumb-item {
  cursor: pointer;
  color: var(--accent0);
  display: flex;
  align-items: center;
  gap: 8px;
  transition: opacity 0.2s;
}

.breadcrumb-item:hover {
  opacity: 0.7;
}

.breadcrumb-item:last-child {
  color: #fff;
  cursor: default;
}

.breadcrumb-item:last-child:hover {
  opacity: 1;
}

.breadcrumb-item i {
  margin-left: 4px;
}

/* Loading */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: var(--accent0);
}

.loading-container p {
  margin-top: 16px;
  font-size: 16px;
}

/* Report Header */
.report-header {
  margin-bottom: 24px;
}

.report-header h2 {
  margin: 0 0 16px 0;
  color: var(--accent0);
}

.summary-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-top: 16px;
}

.stat-card {
  background: var(--dark0);
  padding: 16px;
  border-radius: var(--general-border-radius);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.stat-label {
  font-size: 12px;
  color: #999;
  text-transform: uppercase;
  margin-bottom: 8px;
}

.stat-value {
  font-size: 24px;
  font-weight: bold;
  color: var(--accent1);
}

/* Table */
.report-table-container {
  overflow-x: auto;
  background: var(--dark0);
  border-radius: var(--general-border-radius);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.report-table {
  width: 100%;
  border-collapse: collapse;
  color: black;
}

.report-table thead {
  background: rgba(0, 0, 0, 0.3);
  border-bottom: 2px solid var(--accent0);
}

.report-table th {
  padding: 12px 16px;
  text-align: left;
  font-weight: bold;
  text-transform: uppercase;
  font-size: 12px;
  color: black;
}

.report-table tbody tr {
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  transition: background-color 0.2s;
}

.report-table tbody tr:last-child {
  border-bottom: none;
}

.report-table tbody tr.clickable-row {
  cursor: pointer;
}

.report-table tbody tr.clickable-row:hover {
  background-color: rgba(29, 154, 214, 0.1);
}

.report-table td {
  padding: 12px 16px;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.primary-cell {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--accent0);
  font-weight: 500;
}

.primary-cell i {
  opacity: 0.6;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #999;
}

.empty-state i {
  margin-bottom: 16px;
  opacity: 0.5;
}

.empty-state p {
  font-size: 16px;
}
</style>
